import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { GroupDropIndicator } from '@atlaskit/navigation-system/side-nav-items/drag-and-drop/group-drop-indicator';
import { Box } from '@atlaskit/primitives/box';
import Stack from '@atlaskit/primitives/stack';
import GlobalShortcuts, {
	type GlobalShortcutsItem,
	useGlobalShortcutsData,
} from '@atlassian/global-shortcuts';

import type { GlobalSideNavigationProps, NavItemConfigInternal } from '../../common/types';
import NavItem from '../../common/ui/nav-item';
import { useInViewport } from '../../common/utils/use-in-viewport';
import { useL1DragState } from '../../common/utils/use-l1-drag-state';
import { useModifyNavItemForAnalytics } from '../../common/utils/use-modify-nav-item-for-analytics';
import { useNavAnalytics } from '../../common/utils/use-nav-analytics';
import { useOptions } from '../../controllers/options-provider';
import { usePeekMenuId } from '../../controllers/peek-menu-id-providers';
import { useSideNavData } from '../../controllers/side-nav-data-provider';
import { PEEK_TEST_ID, SHORTCUTS_TEST_ID } from '../constants';
import MoreMenu from '../more-menu';
import { HideFromSidebarButton } from '../more-menu/hide-from-sidebar-button';

export function PostMainItems({
	allNavItems,
	additionalGlobalItemsCount,
	globalItems,
	growthItemsCount,
	growthItemShortcuts,
	shortcuts,
	totalNavItemsCount,
	postItemsElem,
	postMainElem,
	draggingL1Item = false,
}: PostMainItemsProps) {
	const {
		appLabel,
		cloudId,
		orgId,
		isShortcutsStatic,
		disableMenuCustomization,
		l1DragAndDropEnabled,
	} = useOptions();

	const peekMenuId = usePeekMenuId();
	const sideNavMenuData = useSideNavData();

	const {
		fireClickSideNavShortcutItemEvent,
		fireClickSideNavMoreMenuItemEvent,
		fireClickSideNavNavItemEvent,
		fireClickSideNavCustomizeSidebarItemEvent,
	} = useNavAnalytics();

	const { modifyNavItemForAnalytics } = useModifyNavItemForAnalytics();

	const containerRef = useRef(null);
	const { hasBeenInViewport } = useInViewport({
		ref: containerRef,
	});

	const hydratedShortcuts = useGlobalShortcutsData({
		shortcutsConfig: shortcuts,
		cloudId,
		orgId,
		isShortcutsStatic,
	});

	const handleShortcutItemClick = useCallback(
		(shortcutId: string, index: number) => {
			fireClickSideNavShortcutItemEvent(
				shortcutId,
				index,
				hydratedShortcuts,
				sideNavMenuData,
				totalNavItemsCount,
				growthItemsCount,
			);
		},
		[
			fireClickSideNavShortcutItemEvent,
			growthItemsCount,
			hydratedShortcuts,
			sideNavMenuData,
			totalNavItemsCount,
		],
	);

	const shortcutItems = useMemo(() => {
		const availableShortcuts = sideNavMenuData.visibleShortcutMenuIds.reduce(
			(acc: GlobalShortcutsItem[], id: string) => {
				const shortcut = hydratedShortcuts?.[id];
				if (shortcut) {
					acc.push(shortcut);
				}
				return acc;
			},
			[],
		);

		if (growthItemShortcuts) {
			availableShortcuts.push(...Object.values(growthItemShortcuts));
		}

		return availableShortcuts.map((shortcut, index) => ({
			...shortcut,
			onClick: () => {
				shortcut.onClick?.();
				handleShortcutItemClick(shortcut.analyticsName ?? shortcut.label, index);
			},
		}));
	}, [
		sideNavMenuData.visibleShortcutMenuIds,
		growthItemShortcuts,
		hydratedShortcuts,
		handleShortcutItemClick,
	]);

	const { groupDropIndicatorRef } = useL1DragState();

	const handleClickMoreMenuItem = useCallback(() => {
		fireClickSideNavMoreMenuItemEvent(
			globalItems,
			sideNavMenuData,
			totalNavItemsCount,
			additionalGlobalItemsCount,
			growthItemsCount,
		);
	}, [
		additionalGlobalItemsCount,
		fireClickSideNavMoreMenuItemEvent,
		globalItems,
		growthItemsCount,
		sideNavMenuData,
		totalNavItemsCount,
	]);

	const handleClickCustomizeItem = useCallback(() => {
		fireClickSideNavCustomizeSidebarItemEvent(
			globalItems,
			sideNavMenuData,
			totalNavItemsCount,
			additionalGlobalItemsCount,
			growthItemsCount,
		);
	}, [
		additionalGlobalItemsCount,
		fireClickSideNavCustomizeSidebarItemEvent,
		globalItems,
		growthItemsCount,
		sideNavMenuData,
		totalNavItemsCount,
	]);

	const handleClickSideNavItemPeeked = useCallback(() => {
		if (!peekMenuId) {
			return;
		}

		fireClickSideNavNavItemEvent(
			peekMenuId,
			0, // index of peeked item is always 0
			allNavItems,
			globalItems,
			sideNavMenuData,
			totalNavItemsCount,
			additionalGlobalItemsCount,
			true, // isInMore
		);
	}, [
		additionalGlobalItemsCount,
		allNavItems,
		fireClickSideNavNavItemEvent,
		globalItems,
		peekMenuId,
		sideNavMenuData,
		totalNavItemsCount,
	]);

	const peekItem =
		!!peekMenuId &&
		sideNavMenuData.hiddenItemMenuIds.includes(peekMenuId) &&
		modifyNavItemForAnalytics(
			peekMenuId,
			allNavItems[peekMenuId],
			globalItems,
			allNavItems,
			sideNavMenuData.visibleItemMenuIds,
			true, // isInMore
		);

	const moreMenu = (
		<MoreMenu
			items={allNavItems}
			shortcutItems={shortcuts}
			itemIds={sideNavMenuData.hiddenItemMenuIds}
			shortcutItemIds={sideNavMenuData.hiddenShortcutMenuIds}
			appLabel={appLabel}
			onClickMoreMenuTrigger={handleClickMoreMenuItem}
			onClickCustomizeSidebarMenuItem={handleClickCustomizeItem}
		/>
	);

	return (
		<Stack space="space.100">
			{postItemsElem}
			{!!shortcutItems.length && (
				<Box ref={containerRef}>
					<GlobalAppShortcuts shortcutItems={shortcutItems} hasBeenInViewport={hasBeenInViewport} />
				</Box>
			)}
			{peekItem && (
				<Box testId={PEEK_TEST_ID}>
					<NavItem
						key={peekMenuId}
						id={peekMenuId}
						item={peekItem}
						onClick={handleClickSideNavItemPeeked}
						peek
					/>
				</Box>
			)}
			{!disableMenuCustomization &&
				(l1DragAndDropEnabled ? (
					<GroupDropIndicator ref={groupDropIndicatorRef} isActive={draggingL1Item}>
						<Box xcss={cx(draggingL1Item ? styles.hidden : styles.visible)}>{moreMenu}</Box>
						<Box xcss={cx(draggingL1Item ? styles.visible : styles.hidden)}>
							<HideFromSidebarButton />
						</Box>
					</GroupDropIndicator>
				) : (
					moreMenu
				))}
			{postMainElem}
		</Stack>
	);
}

export interface PostMainItemsProps
	extends Pick<
		GlobalSideNavigationProps,
		'shortcuts' | 'postItemsElem' | 'postMainElem' | 'growthItemShortcuts'
	> {
	additionalGlobalItemsCount: number;
	allNavItems: Record<string, NavItemConfigInternal>;
	draggingL1Item?: boolean;
	globalItems: Map<string, NavItemConfigInternal>;
	growthItemsCount: number;
	totalNavItemsCount: number;
}

const styles = cssMap({
	visible: {
		display: 'block',
	},
	hidden: {
		display: 'none',
	},
});

function GlobalAppShortcuts({
	shortcutItems,
	hasBeenInViewport,
}: {
	hasBeenInViewport?: boolean;
	shortcutItems: GlobalShortcutsItem[];
}) {
	const { app, cloudId, userId, orgId } = useOptions();

	const { fireViewedShortcutsEvent } = useNavAnalytics();

	useLayoutEffect(() => {
		if (hasBeenInViewport) {
			fireViewedShortcutsEvent(shortcutItems);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasBeenInViewport]);

	return (
		<GlobalShortcuts
			promoteSite
			orgId={orgId}
			cloudId={cloudId}
			userId={userId}
			product={app}
			forceStatic={true}
			testId={SHORTCUTS_TEST_ID}
			shortcutItems={shortcutItems}
		/>
	);
}
